import React from 'react';
import { DesignBucket, DesignLineItem } from '../../types/bidding';
import { formatCurrency } from '../../utils/bidCalculator';
import './BucketEditor.css';

interface DesignBucketEditorProps {
  bucket: DesignBucket;
  onUpdate: (bucket: DesignBucket) => void;
  readOnly?: boolean;
}

const CATEGORY_LABELS: Record<DesignLineItem['category'], string> = {
  design: 'Design',
  engineering: 'Engineering',
  overhead: 'Overhead',
};

const DesignBucketEditor: React.FC<DesignBucketEditorProps> = ({
  bucket,
  onUpdate,
  readOnly = false,
}) => {
  const handleUpdateLineItem = (index: number, updates: Partial<DesignLineItem>) => {
    const updatedItems = [...bucket.lineItems];
    const item = { ...updatedItems[index], ...updates };
    
    // Recalculate cost
    item.cost = item.dayRate * item.days;
    if (!('billableTotal' in updates)) {
      item.billableTotal = item.cost;
    }
    
    updatedItems[index] = item;
    
    const total = updatedItems.reduce((sum, item) => sum + item.billableTotal, 0);
    onUpdate({ lineItems: updatedItems, total });
  };

  const handleAddLineItem = () => {
    const newItem: DesignLineItem = {
      id: `design-${Date.now()}`,
      category: 'design',
      description: '',
      dayRate: 600,
      days: 0,
      cost: 0,
      billableTotal: 0,
    };
    
    const updatedItems = [...bucket.lineItems, newItem];
    onUpdate({ lineItems: updatedItems, total: bucket.total });
  };

  const handleDeleteLineItem = (index: number) => {
    const updatedItems = bucket.lineItems.filter((_, i) => i !== index);
    const total = updatedItems.reduce((sum, item) => sum + item.billableTotal, 0);
    onUpdate({ lineItems: updatedItems, total });
  };

  return (
    <div className="bucket-editor design-bucket-editor">
      <div className="bucket-header">
        <h4>Design & Engineering</h4>
        <div className="bucket-total">{formatCurrency(bucket.total)}</div>
      </div>

      <table className="bucket-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Description</th>
            <th>Day Rate</th>
            <th>Days</th>
            <th>Cost</th>
            <th>Billable</th>
            {!readOnly && <th></th>}
          </tr>
        </thead>
        <tbody>
          {bucket.lineItems.map((item, index) => (
            <tr key={item.id}>
              <td>
                {readOnly ? (
                  <span className="type-badge">{CATEGORY_LABELS[item.category]}</span>
                ) : (
                  <select
                    value={item.category}
                    onChange={(e) => handleUpdateLineItem(index, { category: e.target.value as DesignLineItem['category'] })}
                  >
                    <option value="design">Design</option>
                    <option value="engineering">Engineering</option>
                    <option value="overhead">Overhead</option>
                  </select>
                )}
              </td>
              <td>
                {readOnly ? (
                  <span>{item.description}</span>
                ) : (
                  <input
                    type="text"
                    value={item.description || ''}
                    onChange={(e) => handleUpdateLineItem(index, { description: e.target.value })}
                    placeholder="Description"
                  />
                )}
              </td>
              <td>
                {readOnly ? (
                  <span>${item.dayRate}/day</span>
                ) : (
                  <input
                    type="number"
                    value={item.dayRate}
                    onChange={(e) => handleUpdateLineItem(index, { dayRate: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="50"
                  />
                )}
              </td>
              <td>
                {readOnly ? (
                  <span>{item.days}</span>
                ) : (
                  <input
                    type="number"
                    value={item.days}
                    onChange={(e) => handleUpdateLineItem(index, { days: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.5"
                  />
                )}
              </td>
              <td className="calculated-cell">
                {formatCurrency(item.cost)}
              </td>
              <td>
                {readOnly ? (
                  <span>{formatCurrency(item.billableTotal)}</span>
                ) : (
                  <input
                    type="number"
                    value={item.billableTotal}
                    onChange={(e) => handleUpdateLineItem(index, { billableTotal: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                  />
                )}
              </td>
              {!readOnly && (
                <td>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteLineItem(index)}
                    title="Delete line"
                  >
                    ×
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {!readOnly && (
        <button className="add-line-btn" onClick={handleAddLineItem}>
          + Add Line Item
        </button>
      )}
    </div>
  );
};

export default DesignBucketEditor;
